import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import {
  bootstrapUser, linkPhone, requestBusiness,
  getMyEvents, cancelEvent, updateEventApi,
  getOpenEvents, joinEventApi, leaveEventApi,
  getBusinessRequests, approveBusiness, rejectBusiness,
  getAllUsersAdmin, setEventLimit,
  getAllEvents, getUsageSummary
} from '../services/api';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

const SPORT_OPTIONS = ['⚽ Fútbol', '🏓 Padel', '🏀 Básquet', '🎾 Tenis', '🚴 Ciclismo', '🏃 Running', '🎵 Social', '🍽️ Comer/Beber'];
const DAYS_ES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

const PADEL_HOLES_MINI = [[40, 24], [50, 22], [60, 24], [34, 32], [50, 42], [66, 32], [50, 60]];
function MiniPadelIcon({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" style={{ display: 'inline', verticalAlign: 'middle', marginRight: 3 }}>
      <rect x="42" y="66" width="16" height="22" rx="7" fill="#2B2118" />
      <ellipse cx="50" cy="40" rx="34" ry="38" fill="#1C1C1C" stroke="#D4843C" strokeWidth="3" />
      {PADEL_HOLES_MINI.map(([x, y], i) => <circle key={i} cx={x} cy={y} r="3" fill="#3D3D3D" />)}
    </svg>
  );
}

function formatDateTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  const dateObj = new Date(y, m - 1, d);
  const dayName = DAYS_ES[dateObj.getDay()];
  return `${dayName} ${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')} ${timeStr}`;
}

// Parsea "Martes 25/06 18:00" → Date (asume el año actual, son turnos de corto plazo)
function parseEventDate(timeStr) {
  if (!timeStr) return null;
  const m = timeStr.match(/(\d{2})\/(\d{2})\s+(\d{2}):(\d{2})/);
  if (!m) return null;
  const [, dd, mm, hh, min] = m;
  const now = new Date();
  return new Date(now.getFullYear(), Number(mm) - 1, Number(dd), Number(hh), Number(min));
}

function hoursUntilEvent(timeStr) {
  const d = parseEventDate(timeStr);
  if (!d) return null;
  return (d.getTime() - Date.now()) / (1000 * 60 * 60);
}

function countdownLabel(timeStr) {
  const hrs = hoursUntilEvent(timeStr);
  if (hrs === null) return null;
  if (hrs < 0) return null;
  if (hrs < 1) return 'Empieza en menos de 1 hora';
  if (hrs < 24) return `Faltan ${Math.round(hrs)} hora${Math.round(hrs) !== 1 ? 's' : ''}`;
  const days = Math.round(hrs / 24);
  return `Faltan ${days} día${days !== 1 ? 's' : ''}`;
}

// ─── ESTILOS COMPARTIDOS ────────────────────────────────────────────────────
const card = { background: '#121A17', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: 20, marginBottom: 16 };
const label = { fontSize: 12, color: '#9CA8A2', marginBottom: 6, display: 'block', fontFamily: "'Nunito', sans-serif" };
const input = { width: '100%', background: '#0A0F0D', border: '1.5px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '10px 12px', color: '#F4F7F5', fontSize: 14, marginBottom: 12, boxSizing: 'border-box', fontFamily: "'Nunito', sans-serif" };
const btnPrimary = { background: '#2EE06B', color: '#F4F7F5', border: 'none', borderRadius: 999, padding: '10px 18px', fontSize: 13, fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, cursor: 'pointer' };
const btnSecondary = { background: 'transparent', color: '#9CA8A2', border: '1.5px solid rgba(255,255,255,0.08)', borderRadius: 999, padding: '10px 18px', fontSize: 13, fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, cursor: 'pointer' };

const GLOBAL_STYLES = `
  .winal-card-hover { transition: transform .15s ease, box-shadow .15s ease, border-color .15s ease; }
  .winal-card-hover:hover { transform: translateY(-2px); box-shadow: 0 10px 28px rgba(255,255,255,0.14); border-color: rgba(46,224,107,0.4); }
  .winal-btn:hover { filter: brightness(1.1); }
  .winal-chip { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 999px; font-size: 11px; font-weight: 600; background: rgba(46,224,107,0.14); color: #2EE06B; white-space: nowrap; }
  .winal-track { height: 6px; border-radius: 999px; background: rgba(255,255,255,0.06); overflow: hidden; margin-top: 10px; }
  .winal-fill { height: 100%; border-radius: 999px; transition: width .4s ease; }
  .winal-fadein { animation: winalFadeIn .35s ease both; }
  @keyframes winalFadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
  .winal-stat-num { transition: color .2s ease; }
`;

// ─── TARJETA DE EVENTO REUTILIZABLE ────────────────────────────────────────
function EventCard({ e, badges = [], actions = [], showOrganizer = true, showSource = false, compact = false, showAttendees = false }) {
  const remaining = Math.max(0, e.totalPlayers - e.joinedPlayers);
  const pct = e.totalPlayers > 0 ? Math.min(100, Math.round((e.joinedPlayers / e.totalPlayers) * 100)) : 0;
  const isFull = remaining === 0;
  const countdown = countdownLabel(e.time);
  const hrs = hoursUntilEvent(e.time);
  const underCapacitySoon = !e.finished && !isFull && hrs !== null && hrs >= 0 && hrs <= 24;

  return (
    <div className="winal-card-hover winal-fadein" style={{ background: '#0A0F0D', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: compact ? 12 : 16, marginBottom: compact ? 8 : 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: compact ? 5 : 8, flexWrap: 'wrap' }}>
            <span className="winal-chip">{e.typeLabel === '🏓 Padel' ? <><MiniPadelIcon />Padel</> : e.typeLabel}</span>
            {e.level && e.level !== 'Libre' && <span className="winal-chip" style={{ background: 'rgba(33,182,201,0.14)', color: '#1A8A99' }}>{e.level}</span>}
            {e.finished && <span className="winal-chip" style={{ background: 'rgba(255,255,255,0.06)', color: '#9CA8A2' }}>Finalizado</span>}
            {isFull && !e.finished && <span className="winal-chip" style={{ background: 'rgba(239,68,68,0.14)', color: '#FF6B6B' }}>Completo</span>}
            {underCapacitySoon && <span className="winal-chip" style={{ background: 'rgba(255,200,87,0.22)', color: '#FFC857' }}>⚠️ Sin completar — revisar</span>}
            {badges.map((b, i) => <span key={i} className="winal-chip" style={b.style}>{b.label}</span>)}
          </div>
          <div style={{ color: '#F4F7F5', fontSize: compact ? 14 : 15.5, fontWeight: 700, marginBottom: 5, fontFamily: "'Baloo 2', sans-serif" }}>{e.title}</div>
          <div style={{ color: '#9CA8A2', fontSize: compact ? 11.5 : 12.5, lineHeight: 1.6 }}>
            🕐 {e.time} &nbsp;·&nbsp; 📍 {e.location}
            {showOrganizer && e.organizer ? <> &nbsp;·&nbsp; Organiza: {e.organizer}</> : null}
            {showSource ? <> &nbsp;·&nbsp; vía {e.source || 'whatsapp'}</> : null}
            &nbsp;·&nbsp; <a href={e.mapsUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(e.location || '')}`} target="_blank" rel="noopener noreferrer" style={{ color: '#2EE06B', fontWeight: 700, textDecoration: 'none' }}>Ver en el mapa</a>
          </div>
          {countdown && !e.finished && <div style={{ color: '#9CA8A2', fontSize: 11, marginTop: 3, fontWeight: 700 }}>⏳ {countdown}</div>}
          <div className="winal-track" style={{ maxWidth: 240 }}>
            <div className="winal-fill" style={{ width: `${pct}%`, background: isFull ? '#FF6B6B' : '#2EE06B' }} />
          </div>
          <div style={{ color: '#9CA8A2', fontSize: 11, marginTop: 5 }}>
            {e.joinedPlayers}/{e.totalPlayers} anotados{!isFull && !e.finished ? ` · faltan ${remaining}` : ''}
          </div>
          {showAttendees && (e.attendeeProfiles || []).length > 0 && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              {e.attendeeProfiles.map((p, i) => (
                <div key={i} style={{ fontSize: 11, color: '#9CA8A2' }}>👤 {p.name || 'Sin nombre'} — {p.emailKey}</div>
              ))}
            </div>
          )}
        </div>
        {actions.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {actions.map((a, i) => (
              <button key={i} className="winal-btn" style={a.style || btnSecondary} onClick={a.onClick}>{a.label}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── FORMULARIO DE EDICIÓN INLINE ───────────────────────────────────────────
function EditEventForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState({ eventDate: '', eventTime: '', location: initial.location, totalPlayers: String(initial.totalPlayers), shortComment: initial.shortComment || '' });
  return (
    <div className="winal-fadein" style={{ background: '#0A0F0D', border: '1px solid #2EE06B', borderRadius: 10, padding: 16, marginBottom: 10 }}>
      <div style={{ color: '#F4F7F5', fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Editando: {initial.title}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input style={{ ...input, flex: 1 }} type="date" value={f.eventDate} onChange={ev => setF({ ...f, eventDate: ev.target.value })} />
        <input style={{ ...input, flex: 1 }} type="time" value={f.eventTime} onChange={ev => setF({ ...f, eventTime: ev.target.value })} />
      </div>
      <p style={{ color: '#9CA8A2', fontSize: 11, marginTop: -8, marginBottom: 10 }}>Dejá fecha y hora vacías si no las querés cambiar — hoy es: {initial.time}</p>
      <input style={input} placeholder="Lugar" value={f.location} onChange={ev => setF({ ...f, location: ev.target.value })} />
      <input style={input} type="number" min={initial.joinedPlayers} placeholder="Total de personas" value={f.totalPlayers} onChange={ev => setF({ ...f, totalPlayers: ev.target.value })} />
      <input style={input} placeholder="Comentario" value={f.shortComment} onChange={ev => setF({ ...f, shortComment: ev.target.value })} />
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="winal-btn" style={btnPrimary} onClick={() => onSave(f)}>Guardar cambios</button>
        <button className="winal-btn" style={btnSecondary} onClick={onCancel}>Cancelar</button>
      </div>
    </div>
  );
}

// ─── BARRA DE BÚSQUEDA + FILTRO ─────────────────────────────────────────────
function SearchFilterBar({ search, setSearch, sport, setSport, placeholder }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
      <input
        style={{ ...input, marginBottom: 0, flex: '1 1 200px' }}
        placeholder={placeholder || 'Buscar por lugar o nombre...'}
        value={search}
        onChange={e => setSearch(e.target.value)}
      />
      <select style={{ ...input, marginBottom: 0, width: 160, flex: '0 0 auto' }} value={sport} onChange={e => setSport(e.target.value)}>
        <option value="Todos">Todos los deportes</option>
        {SPORT_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

// ─── STAT CHIP DE RESUMEN ───────────────────────────────────────────────────
function StatCard({ number, label, accent }) {
  return (
    <div className="winal-card-hover winal-fadein" style={{ ...card, marginBottom: 0, flex: 1, minWidth: 140 }}>
      <div className="winal-stat-num" style={{ fontSize: 28, fontWeight: 700, color: accent ? '#2EE06B' : '#F4F7F5', fontFamily: "'Baloo 2', sans-serif" }}>{number}</div>
      <div style={{ fontSize: 12, color: '#9CA8A2', marginTop: 2 }}>{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [events, setEvents] = useState([]);
  const [openEvents, setOpenEvents] = useState([]);
  const [phoneInput, setPhoneInput] = useState('');
  const [editingId, setEditingId] = useState(null);

  const [exploreSearch, setExploreSearch] = useState('');
  const [exploreSport, setExploreSport] = useState('Todos');

  const [businessSent, setBusinessSent] = useState(false);

  const [tab, setTab] = useState('eventos');
  const [allEvents, setAllEvents] = useState([]);
  const [adminSearch, setAdminSearch] = useState('');
  const [adminSport, setAdminSport] = useState('Todos');
  const [businessRequests, setBusinessRequests] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [usage, setUsage] = useState([]);

  const refreshMyEvents = useCallback(async () => {
    try { setEvents(await getMyEvents()); } catch (e) { /* silencioso */ }
  }, []);

  const refreshOpenEvents = useCallback(async () => {
    try { setOpenEvents(await getOpenEvents()); } catch (e) { /* silencioso */ }
  }, []);

  useEffect(() => {
    const init = async () => {
      if (!user?.email) return;
      try {
        const data = await bootstrapUser(user.email, user.displayName);
        setMe(data);
      } catch (e) {
        setError('No pudimos cargar tu cuenta. Recargá la página.');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [user]);

  useEffect(() => {
    if (me?.phone) { refreshMyEvents(); refreshOpenEvents(); }
  }, [me, refreshMyEvents, refreshOpenEvents]);

  useEffect(() => {
    if (me?.role !== 'superadmin') return;
    if (tab === 'eventos') getAllEvents().then(setAllEvents).catch(() => {});
    if (tab === 'negocio') getBusinessRequests().then(setBusinessRequests).catch(() => {});
    if (tab === 'usuarios') getAllUsersAdmin().then(setAllUsers).catch(() => {});
    if (tab === 'costos') getUsageSummary().then(setUsage).catch(() => {});
  }, [me, tab]);

  const handleLinkPhone = async () => {
    if (!phoneInput.trim()) return;
    try {
      const res = await linkPhone(phoneInput.trim());
      setMe({ ...me, phone: res.phone });
      setPhoneInput('');
    } catch (e) {
      setError('No pudimos vincular ese número.');
    }
  };

  

  const handleCancelEvent = async (id) => {
    if (!window.confirm('¿Seguro que querés cancelar este evento? Esta acción no se puede deshacer.')) return;
    try { await cancelEvent(id); refreshMyEvents(); refreshOpenEvents(); } catch (e) { setError('No pudimos cancelar el evento.'); }
  };

  const handleSaveEdit = async (id, f) => {
    setError('');
    const time = f.eventDate && f.eventTime ? formatDateTime(f.eventDate, f.eventTime) : undefined;
    try {
      await updateEventApi(id, {
        ...(time ? { time } : {}),
        location: f.location,
        totalPlayers: Number(f.totalPlayers),
        shortComment: f.shortComment
      });
      setEditingId(null);
      refreshMyEvents();
      refreshOpenEvents();
    } catch (e) {
      setError(e.response?.data?.error || 'No pudimos guardar los cambios.');
    }
  };

  const handleJoinEvent = async (id) => {
    setError('');
    try { await joinEventApi(id); refreshMyEvents(); refreshOpenEvents(); } catch (e) {
      setError(e.response?.data?.error || 'No pudimos sumarte a ese evento.');
    }
  };

  const handleLeaveEvent = async (id) => {
    setError('');
    try { await leaveEventApi(id); refreshMyEvents(); refreshOpenEvents(); } catch (e) {
      setError(e.response?.data?.error || 'No pudimos sacarte de ese evento.');
    }
  };

  const handleRequestBusiness = async () => {
    try { await requestBusiness(''); setBusinessSent(true); } catch (e) { setError('No pudimos enviar la solicitud.'); }
  };

  const handleApprove = async (id) => { await approveBusiness(id); setBusinessRequests(await getBusinessRequests()); };
  const handleReject = async (id) => { await rejectBusiness(id); setBusinessRequests(await getBusinessRequests()); };
  const handleSetLimit = async (id, value) => { await setEventLimit(id, value); setAllUsers(await getAllUsersAdmin()); };

  const content = { marginLeft: isMobile ? 0 : 210, paddingTop: isMobile ? 56 : 0, padding: 28, minHeight: '100vh', background: '#0A0F0D', fontFamily: "'Nunito', sans-serif" };

  if (loading) {
    return (
      <>
        <Sidebar active="dashboard" />
        <div style={content}><p style={{ color: '#9CA8A2' }}>Cargando...</p></div>
      </>
    );
  }

  const isPersonalOrNegocio = me?.role === 'personal' || me?.role === 'negocio';

  const filterEvents = (list, search, sport) => list.filter(e => {
    const matchSport = sport === 'Todos' || e.typeLabel === sport;
    const needle = search.toLowerCase().trim();
    const matchSearch = !needle || (e.location || '').toLowerCase().includes(needle) || (e.title || '').toLowerCase().includes(needle);
    return matchSport && matchSearch;
  });

  const filteredOpenEvents = filterEvents(openEvents, exploreSearch, exploreSport);
  const filteredAllEvents = filterEvents(allEvents, adminSearch, adminSport);

  const myOrganized = events.filter(e => !e.finished && e.organizerPhone === me?.phone);
  const myJoined = events.filter(e => !e.finished && e.organizerPhone !== me?.phone);

  // Stats específicos de Negocio
  const myVenueEvents = events.filter(e => e.organizerPhone === me?.phone && e.courtId);
  const sportBreakdown = {};
  myOrganized.forEach(e => { sportBreakdown[e.typeLabel] = (sportBreakdown[e.typeLabel] || 0) + 1; });
  const jugadoresRegistrados = myOrganized.reduce((sum, e) => sum + (e.joinedPlayers || 0), 0);
  const turnosSinCompletar = myOrganized.filter(e => {
    const hrs = hoursUntilEvent(e.time);
    return e.joinedPlayers < e.totalPlayers && hrs !== null && hrs >= 0 && hrs <= 24;
  });
  const turnosCompletados = myVenueEvents.filter(e => e.joinedPlayers >= e.totalPlayers).length;
  const clientesUnicos = new Set();
  myVenueEvents.forEach(e => (e.attendeeProfiles || []).forEach(p => p.emailKey && clientesUnicos.add(p.emailKey)));

  return (
    <>
      <style>{GLOBAL_STYLES}</style>
      <Sidebar active="dashboard" />
      <div style={content}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
          <div>
            <h1 style={{ color: '#F4F7F5', fontSize: 28, fontWeight: 700, marginBottom: 4, fontFamily: "'Baloo 2', sans-serif" }}>
              Hola, {(user?.displayName || me?.name || '').split(' ')[0]}
            </h1>
            <p style={{ color: '#9CA8A2', fontSize: 13 }}>
              Cuenta: {me?.role === 'negocio' ? 'Negocio · eventos ilimitados' : me?.role === 'superadmin' ? 'Super Admin' : 'Personal'}
            </p>
          </div>

          {me?.role === 'personal' && (
            businessSent || me.businessRequestStatus === 'pending' ? (
              <div style={{ ...card, margin: 0, padding: '10px 16px', maxWidth: 280 }}>
                <p style={{ color: '#2EE06B', fontSize: 12, margin: 0 }}>✅ Solicitud de Negocio enviada — te contactamos pronto.</p>
              </div>
            ) : (
              <div style={{ ...card, margin: 0, padding: 14, maxWidth: 300, textAlign: 'right' }}>
                <p style={{ color: '#9CA8A2', fontSize: 12, marginBottom: 8 }}>
                  ¿Organizás más de 5 eventos al mes? Solicitá mayor disponibilidad.
                </p>
                <button className="winal-btn" style={btnPrimary} onClick={handleRequestBusiness}>Quiero ser Negocio</button>
              </div>
            )
          )}
        </div>

        {error && <div style={{ ...card, borderColor: '#FF6B6B', color: '#FF6B6B' }}>{error}</div>}

        {isPersonalOrNegocio && !me?.phone && (
          <div style={card}>
            <h3 style={{ color: '#F4F7F5', marginBottom: 8, fontFamily: "'Baloo 2', sans-serif" }}>Vinculá tu WhatsApp</h3>
            <p style={{ color: '#9CA8A2', fontSize: 13, marginBottom: 12 }}>
              Necesitamos tu número para que tus eventos por WhatsApp y por la web sean los mismos.
            </p>
            <input style={input} placeholder="+54 9 11 ..." value={phoneInput} onChange={e => setPhoneInput(e.target.value)} />
            <button className="winal-btn" style={btnPrimary} onClick={handleLinkPhone}>Vincular</button>
          </div>
        )}

        {isPersonalOrNegocio && me?.phone && (
          <>
            {me.role === 'personal' && (
              <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                <StatCard number={myOrganized.length} label="Eventos que organizás" accent />
                <StatCard number={myJoined.length} label="Eventos en los que participás" />
                <StatCard number={openEvents.length} label="Eventos abiertos cerca" />
              </div>
            )}

            {me.role === 'negocio' && (
              <>
                <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                  <StatCard number={myOrganized.length} label="Eventos activos" accent />
                  <StatCard number={jugadoresRegistrados} label="Jugadores registrados" />
                  <StatCard number={turnosSinCompletar.length} label="Sin completar (24hs)" />
                </div>
                {Object.keys(sportBreakdown).length > 0 && (
                  <div style={{ ...card, padding: '12px 16px', marginBottom: 12 }}>
                    <span style={{ fontSize: 11.5, color: '#9CA8A2', fontWeight: 700 }}>Por deporte: </span>
                    {Object.entries(sportBreakdown).map(([sport, count]) => (
                      <span key={sport} style={{ fontSize: 12.5, color: '#F4F7F5', marginRight: 14 }}>{sport}: <strong>{count}</strong></span>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                  <div style={{ ...card, marginBottom: 0, flex: 1, minWidth: 220, background: 'rgba(46,224,107,0.08)', borderColor: 'rgba(46,160,67,0.3)' }}>
                    <div style={{ fontSize: 11, color: '#1F9E4D', fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.3 }}>Impacto de Winal</div>
                    <div style={{ display: 'flex', gap: 24 }}>
                      <div>
                        <div style={{ fontFamily: "'Baloo 2', sans-serif", fontSize: 22, fontWeight: 700, color: '#2EE06B' }}>{clientesUnicos.size}</div>
                        <div style={{ fontSize: 11, color: '#9CA8A2' }}>Clientes sumados</div>
                      </div>
                      <div>
                        <div style={{ fontFamily: "'Baloo 2', sans-serif", fontSize: 22, fontWeight: 700, color: '#2EE06B' }}>{turnosCompletados}</div>
                        <div style={{ fontSize: 11, color: '#9CA8A2' }}>Turnos completados</div>
                      </div>
                    </div>
                  </div>
                </div>
                {turnosSinCompletar.length > 0 && (
                  <div style={{ background: 'rgba(255,200,87,0.16)', border: '1px solid rgba(255,200,87,0.5)', borderRadius: 12, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#FFC857' }}>
                    ⚠️ Tenés {turnosSinCompletar.length} turno{turnosSinCompletar.length !== 1 ? 's' : ''} que empieza{turnosSinCompletar.length !== 1 ? 'n' : ''} en menos de 24hs y no {turnosSinCompletar.length !== 1 ? 'completaron' : 'completó'} el cupo. Revisalos abajo para decidir si los cancelás.
                  </div>
                )}
              </>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: isMobile || me.role === 'negocio' ? '1fr' : '420px 1fr', gap: 16, alignItems: 'start' }}>
              {me.role === 'personal' && (
                <div style={card}>
                  <h3 style={{ color: '#F4F7F5', marginBottom: 10, fontFamily: "'Baloo 2', sans-serif" }}>Crear evento</h3>
                  <p style={{ color: '#9CA8A2', fontSize: 13.5, lineHeight: 1.6 }}>
                    Por ahora, crear eventos nuevos es solo para cuentas Negocio (clubes y canchas) — estamos puliendo el flujo de creación antes de abrirlo a todos.
                  </p>
                  <p style={{ color: '#9CA8A2', fontSize: 13.5, lineHeight: 1.6, marginTop: 10 }}>
                    Mientras tanto, buscá y sumate a los eventos ya publicados en "Explorar eventos" →
                  </p>
                </div>
              )}

              <div>
                <div style={card}>
                  <h3 style={{ color: '#F4F7F5', marginBottom: 14, fontFamily: "'Baloo 2', sans-serif" }}>Mis eventos activos</h3>
                  {myOrganized.length + myJoined.length === 0 ? (
                    <p style={{ color: '#9CA8A2', fontSize: 13 }}>No tenés eventos activos todavía. Creá uno o explorá los de abajo.</p>
                  ) : (
                    [...myOrganized, ...(me.role === 'personal' ? myJoined : [])].map(e => (
                      editingId === e.id ? (
                        <EditEventForm
                          key={e.id}
                          initial={e}
                          onSave={(f) => handleSaveEdit(e.id, f)}
                          onCancel={() => setEditingId(null)}
                        />
                      ) : (
                        <EventCard
                          key={e.id}
                          e={e}
                          compact={me.role === 'negocio'}
                          showAttendees={me.role === 'negocio'}
                          showOrganizer={false}
                          badges={e.organizerPhone !== me.phone ? [{ label: 'Participás', style: { background: 'rgba(255,255,255,0.06)', color: '#9CA8A2' } }] : []}
                          actions={
                            e.organizerPhone === me.phone
                              ? [
                                  { label: 'Editar', onClick: () => setEditingId(e.id), style: btnSecondary },
                                  { label: 'Cancelar', onClick: () => handleCancelEvent(e.id), style: btnSecondary }
                                ]
                              : [{ label: 'Salir', onClick: () => handleLeaveEvent(e.id), style: btnSecondary }]
                          }
                        />
                      )
                    ))
                  )}
                </div>

                {me.role === 'personal' && (
                  <div style={card}>
                    <h3 style={{ color: '#F4F7F5', marginBottom: 14, fontFamily: "'Baloo 2', sans-serif" }}>Explorar eventos</h3>
                    <SearchFilterBar search={exploreSearch} setSearch={setExploreSearch} sport={exploreSport} setSport={setExploreSport} />
                    {filteredOpenEvents.length === 0 ? (
                      <p style={{ color: '#9CA8A2', fontSize: 13 }}>
                        {openEvents.length === 0 ? 'No hay eventos abiertos con cupo en este momento.' : 'Nada coincide con ese filtro.'}
                      </p>
                    ) : (
                      filteredOpenEvents.map(e => {
                        const alreadyJoined = (e.attendeeProfiles || []).some(p => p.emailKey === me.phone);
                        return (
                          <EventCard
                            key={e.id}
                            e={e}
                            actions={[
                              alreadyJoined
                                ? { label: 'Salir', onClick: () => handleLeaveEvent(e.id), style: btnSecondary }
                                : { label: 'Sumarme', onClick: () => handleJoinEvent(e.id), style: btnPrimary }
                            ]}
                          />
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </div>

          </>
        )}

        {me?.role === 'superadmin' && (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              {[['eventos', 'Eventos'], ['negocio', 'Solicitudes Negocio'], ['usuarios', 'Usuarios'], ['costos', 'Costos']].map(([k, lbl]) => (
                <button key={k} className="winal-btn" onClick={() => setTab(k)} style={{
                  ...btnSecondary, color: tab === k ? '#2EE06B' : '#9CA8A2',
                  borderColor: tab === k ? '#2EE06B' : 'rgba(255,255,255,0.08)'
                }}>{lbl}</button>
              ))}
            </div>

            {tab === 'eventos' && (
              <div style={card}>
                <h3 style={{ color: '#F4F7F5', marginBottom: 14, fontFamily: "'Baloo 2', sans-serif" }}>Todos los eventos</h3>
                <SearchFilterBar search={adminSearch} setSearch={setAdminSearch} sport={adminSport} setSport={setAdminSport} placeholder="Buscar por lugar, título u organizador..." />
                {filteredAllEvents.length === 0 ? (
                  <p style={{ color: '#9CA8A2', fontSize: 13 }}>{allEvents.length === 0 ? 'No hay eventos todavía.' : 'Nada coincide con ese filtro.'}</p>
                ) : (
                  filteredAllEvents.map(e => (
                    <EventCard
                      key={e.id}
                      e={e}
                      showSource
                      actions={!e.finished ? [{ label: 'Cancelar', onClick: () => { if (window.confirm('¿Cancelar este evento?')) cancelEvent(e.id).then(() => getAllEvents().then(setAllEvents)); }, style: btnSecondary }] : []}
                    />
                  ))
                )}
              </div>
            )}

            {tab === 'negocio' && (
              <div style={card}>
                <h3 style={{ color: '#F4F7F5', marginBottom: 14, fontFamily: "'Baloo 2', sans-serif" }}>Solicitudes pendientes</h3>
                {businessRequests.length === 0 ? <p style={{ color: '#9CA8A2', fontSize: 13 }}>No hay solicitudes pendientes.</p> : businessRequests.map(u => (
                  <div key={u.id} className="winal-fadein" style={{ padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <div style={{ color: '#F4F7F5', fontSize: 14 }}>{u.name} — {u.email} — {u.phone || 'sin WhatsApp vinculado'}</div>
                    {u.businessRequestNote && <div style={{ color: '#9CA8A2', fontSize: 12, marginBottom: 8 }}>"{u.businessRequestNote}"</div>}
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button className="winal-btn" style={btnPrimary} onClick={() => handleApprove(u.id)}>Aprobar</button>
                      <button className="winal-btn" style={btnSecondary} onClick={() => handleReject(u.id)}>Rechazar</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === 'usuarios' && (
              <div style={card}>
                <h3 style={{ color: '#F4F7F5', marginBottom: 14, fontFamily: "'Baloo 2', sans-serif" }}>Usuarios de Winal</h3>
                {allUsers.filter(u => ['personal', 'negocio'].includes(u.role)).map(u => (
                  <div key={u.id} className="winal-fadein" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <div>
                      <div style={{ color: '#F4F7F5', fontSize: 14 }}>{u.name} — {u.email || u.phone}</div>
                      <div style={{ color: '#9CA8A2', fontSize: 12 }}>Rol: {u.role}</div>
                    </div>
                    {u.role === 'personal' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ color: '#9CA8A2', fontSize: 12 }}>Límite/mes:</span>
                        <input
                          type="number" defaultValue={u.monthlyEventLimit ?? 5}
                          style={{ ...input, width: 60, marginBottom: 0 }}
                          onBlur={(e) => handleSetLimit(u.id, e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {tab === 'costos' && (
              <div style={card}>
                <h3 style={{ color: '#F4F7F5', marginBottom: 8, fontFamily: "'Baloo 2', sans-serif" }}>Costos estimados — últimos 12 meses</h3>
                <p style={{ color: '#9CA8A2', fontSize: 12, marginBottom: 14 }}>
                  Estimado con tarifas de referencia. Ajustalas en <code>backend/src/controllers/usageController.js</code> con tus precios reales.
                </p>
                {usage.length === 0 ? <p style={{ color: '#9CA8A2', fontSize: 13 }}>Todavía no hay datos de uso.</p> : usage.map(m => (
                  <div key={m.month} className="winal-fadein" style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <div style={{ color: '#F4F7F5', fontSize: 14 }}>{m.month}</div>
                    <div style={{ color: '#9CA8A2', fontSize: 12 }}>{m.aiCalls} llamadas IA · {m.messagesOutbound} msjs WhatsApp</div>
                    <div style={{ color: '#2EE06B', fontSize: 14, fontWeight: 600 }}>≈ ${m.totalCostEstimate}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}